/* istanbul ignore file */
import React, { useCallback, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { alpha } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import Logo from "../../assets/images/TrueValueLogo.svg";
import LogoutButton from "../../components/LogoutButton";
import PermissionsGate from "../../permissions/PermissionsGate";
import { SCOPES } from "../../permissions/permissions-map";
import {
  resetDepartmentFilters,
  resetItemFilters,
  resetPogFilters,
} from "../../store/globalSlice";
import { isFeatureActive } from "../../utils/featureFlag";

const highlightHeaderSelection = {
  textDecoration: "none",
  color: "#fff",
  backgroundColor: "rgba(255, 255, 255, 0.3)",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.3)",
  },
};

const headerDefaultStyle = {
  textDecoration: "none",
  color: "#fff",
};

const getActiveButtonStyle = (matchFound) => ({
  marginRight: 4,
  paddingY: 1.2,
  borderRadius: 0,
  ...(matchFound && highlightHeaderSelection),
});

export default function Header() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { isAuthenticated } = useSelector((store) => store.global.authState);
  const store = useSelector((state) => state.global.store);
  const user = useSelector((state) => state.global.user);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [anchorElOffline, setAnchorElOffline] = React.useState(null);
  const open = Boolean(anchorEl);
  const offlineOpen = Boolean(anchorElOffline);

  const handleOfflineClick = (event) => {
    setAnchorElOffline(event.currentTarget);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setAnchorElOffline(null);
  };

  const storeDetails = useMemo(() => {
    let details = "";
    if (!!store.memberNumber) {
      details += `Member Number: ${store.memberNumber} `;
    }
    if (!!store.rdc) {
      details +=
        details.length > 0 ? `| RDC: ${store.rdc} ` : `RDC: ${store.rdc} `;
    }
    if (!!store.name) {
      details +=
        details.length > 0
          ? `| Store Name: ${store.name}`
          : `Store Name: ${store.name}`;
    }
    return details;
  }, [store.memberNumber, store.name, store.rdc]);

  const storeExists = useMemo(() => !!store.id, [store.id]);

  const notStorePage = useMemo(
    () => storeExists && !pathname.includes("/admin"),
    [storeExists, pathname]
  );

  const getActiveButtonStyles = useCallback(
    (path) => {
      if (
        !pathname.includes("/manage-planograms") &&
        !pathname.includes("/store-supplies")
      ) {
        dispatch(resetDepartmentFilters());
        dispatch(resetPogFilters());
        dispatch(resetItemFilters());
      }
      return getActiveButtonStyle(
        path === "/" ? path === pathname : pathname.includes(path)
      );
    },
    [pathname, dispatch]
  );

  if (!isAuthenticated) {
    return <></>;
  }

  return (
    <AppBar position="sticky">
      <Toolbar variant="dense" sx={{ backgroundColor: "#fff" }}>
        <Grid container spacing={1}>
          <Grid
            item
            xs={12}
            sm={notStorePage ? 6 : 8}
            md={notStorePage ? 4 : 8}
            lg={notStorePage ? 4 : 8}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <img src={Logo} alt="TrueValue" className="tvLogo" />
            <Typography variant="h6" marginLeft={2} color="textPrimary">
              OPENING STOCK ORDER
            </Typography>
          </Grid>
          {notStorePage && (
            <Grid
              item
              xs={8}
              sm={4}
              md={6}
              lg={6}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Chip
                icon={
                  <InfoIcon
                    sx={{
                      color: (theme) =>
                        `${alpha(theme.palette.primary.main, 0.8)} !important`,
                    }}
                  />
                }
                variant="outlined"
                label={storeDetails}
                title={storeDetails}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: (theme) => alpha(theme.palette.primary.main, 0.8),
                  fontWeight: 600,
                }}
              />
            </Grid>
          )}
          <Grid
            item
            xs={4}
            sm={notStorePage ? 2 : 8}
            md={notStorePage ? 2 : 8}
            lg={notStorePage ? 2 : 4}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <AccountCircleOutlinedIcon
              fontSize="large"
              sx={{ color: (theme) => theme.palette.primary.main }}
            />
            <Box
              marginLeft={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography variant="body2" color="textPrimary">
                {user.uname}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Toolbar>
      <Toolbar
        variant="dense"
        sx={{ minHeight: 0, display: "flex", justifyContent: "space-between" }}
      >
        <Box sx={{ display: "flex" }}>
          <NavLink to="/" style={headerDefaultStyle}>
            <Button
              color="inherit"
              size="small"
              sx={getActiveButtonStyles("/")}
              disableRipple
            >
              Home
            </Button>
          </NavLink>
          {storeExists && (
            <>
              <NavLink to="project-details" style={headerDefaultStyle}>
                <Button
                  color="inherit"
                  size="small"
                  sx={getActiveButtonStyles("/project-details")}
                  disableRipple
                >
                  Details
                </Button>
              </NavLink>
              {!store.isStoreSupplies && (
                <NavLink
                  to="manage-planograms/departments"
                  style={headerDefaultStyle}
                >
                  <Button
                    color="inherit"
                    size="small"
                    sx={getActiveButtonStyles("/manage-planograms")}
                    disableRipple
                  >
                    Planograms
                  </Button>
                </NavLink>
              )}
              <NavLink to="store-supplies/pogs" style={headerDefaultStyle}>
                <Button
                  color="inherit"
                  size="small"
                  sx={getActiveButtonStyles("/store-supplies")}
                  disableRipple
                >
                  Store Supplies
                </Button>
              </NavLink>
            </>
          )}

          <NavLink to="project-reports" style={headerDefaultStyle}>
            <Button
              color="inherit"
              size="small"
              sx={getActiveButtonStyles("/project-reports")}
              disableRipple
            >
              Reports
            </Button>
          </NavLink>

          <PermissionsGate scopes={[SCOPES.canAll]}>
            <Button
              id="basic-button"
              aria-controls={open ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleClick}
              color="inherit"
              size="small"
              sx={getActiveButtonStyles("/admin")}
              disableRipple
            >
              Admin
            </Button>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <NavLink to="admin/manage-notes" style={headerDefaultStyle}>
                <MenuItem
                  onClick={handleClose}
                  sx={{ color: "#c60c30", fontSize: 14 }}
                  selected={pathname === "/admin/manage-notes"}
                >
                  MANAGE NOTES
                </MenuItem>
              </NavLink>
            </Menu>
          </PermissionsGate>

          {isFeatureActive() && storeExists && false && (
            <>
              <Button
                id="basic-button"
                aria-controls={offlineOpen ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={offlineOpen ? "true" : undefined}
                onClick={handleOfflineClick}
                color="inherit"
                size="small"
                sx={getActiveButtonStyles("/offline")}
                disableRipple
              >
                Offline
              </Button>
              <Menu
                id="basic-menu"
                anchorEl={anchorElOffline}
                open={offlineOpen}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
              >
                <NavLink
                  to="offline/sync-to-offline"
                  style={headerDefaultStyle}
                >
                  <MenuItem
                    onClick={handleClose}
                    sx={{ color: "#c60c30", fontSize: 14 }}
                    selected={pathname === "/offline/sync-to-offline"}
                  >
                    SYNC TO OFFLINE
                  </MenuItem>
                </NavLink>
                <NavLink to="offline/sync-to-online" style={headerDefaultStyle}>
                  <MenuItem
                    onClick={handleClose}
                    sx={{ color: "#c60c30", fontSize: 14 }}
                    selected={pathname === "/offline/sync-to-online"}
                  >
                    SYNC TO ONLINE
                  </MenuItem>
                </NavLink>
              </Menu>
            </>
          )}
        </Box>
        <LogoutButton />
      </Toolbar>
    </AppBar>
  );
}
